// /src/components/LpLeadModal.jsx
// Modal di lead generation TEMATIZZATO per le Landing Pages /lp/*.
// Eredita i colori del tema della LP via CSS variables (--lp-primary, --lp-card, ...)
// del container `[data-theme]` parente, e usa `copy` props per personalizzare testi
// (kicker / heading / subheading / submit / success).
//
// Tracking: spara `Lead` standard event → pipeline esistente in tracking.js (Meta Pixel
// client + Meta CAPI server + GA4 + Google Ads conversion `AW-17483775489/...`
// con Enhanced Conversions su email/phone).
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { trackEventDedup, persistLeadData } from '../lib/tracking';

const INITIAL = { nome: '', cognome: '', email: '', telefono: '', privacy: false };

const validateForm = (form) => {
  if (!form.nome.trim() || form.nome.trim().length < 2) return 'Inserisci il tuo nome.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Inserisci una email valida.';
  const phone = form.telefono.replace(/[\s().-]/g, '');
  if (!/^\+[1-9]\d{6,14}$/.test(phone)) return 'Inserisci il telefono con prefisso internazionale (es. +393331234567).';
  if (!form.privacy) return 'Devi accettare la Privacy Policy per ricevere il coupon.';
  return '';
};

const LpLeadModal = ({ open, onClose, source, copy }) => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (open) trackEventDedup('FormView', { source: source || 'lp-unknown', form_type: 'lp-lead' });
  }, [open, source]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    const value = field === 'privacy' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    if (!startedRef.current) {
      startedRef.current = true;
      trackEventDedup('FormStart', { source: source || 'lp-unknown', first_field: field, form_type: 'lp-lead' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm(form);
    if (err) { setErrorMsg(err); return; }
    setErrorMsg(''); setSubmitting(true);
    const payload = {
      first_name: form.nome.trim(),
      last_name: form.cognome.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.telefono.replace(/[\s().-]/g, ''),
      privacy_consent: form.privacy,
      source: source || 'lp-unknown',
      offer: copy?.offer_tag || '1 bibita in omaggio sul primo ordine',
      value: 0,
      currency: 'EUR',
    };
    try {
      await trackEventDedup('Lead', payload);
      persistLeadData(payload);
      setSuccess(true);
    } catch (_err) {
      setErrorMsg('Si è verificato un errore. Riprova tra qualche istante.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="lp-lead-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-2xl shadow-2xl border overflow-hidden animate-[fadeIn_.25s_ease-out]"
        style={{ background: 'var(--lp-card, #fff)', borderColor: 'var(--lp-border, #EDE8E0)' }}>
        <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="lp-lead-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-white border border-black/5 flex items-center justify-center">
          <X size={18} className="text-[#1A1A1A]" />
        </button>

        {/* Banner colorato con i colori del tema della LP */}
        <div className="px-6 py-7 text-white" style={{ background: 'var(--lp-primary)' }}>
          <p className="font-['Poppins',_sans-serif] uppercase text-[11px] tracking-[0.25em] opacity-90 mb-2" data-testid="lp-lead-kicker">
            {copy?.kicker || 'Bibita in omaggio'}
          </p>
          <h3 className="font-['Poppins',_sans-serif] font-black text-2xl md:text-3xl leading-tight" data-testid="lp-lead-heading">
            {copy?.heading || 'Ricevi la tua bibita in omaggio'}
          </h3>
          {copy?.subheading && (
            <p className="font-['Lora',_serif] text-sm md:text-base opacity-95 mt-3" data-testid="lp-lead-subheading">
              {copy.subheading}
            </p>
          )}
        </div>

        <div className="p-6 md:p-8">
          {success ? (
            <div data-testid="lp-lead-success" className="text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5 border-2"
                style={{ background: 'rgba(0,0,0,0.04)', borderColor: 'var(--lp-primary)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--lp-primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h4 className="font-['Poppins',_sans-serif] font-bold text-2xl mb-3" style={{ color: 'var(--lp-fg, #1A1A1A)' }}>
                {copy?.success_title || `Grazie ${form.nome}!`}
              </h4>
              <p className="font-['Lora',_serif] text-base leading-relaxed mb-6" style={{ color: 'var(--lp-muted, #5a4a42)' }}>
                {copy?.success_body || 'Riceverai a breve il tuo coupon su WhatsApp al numero che hai indicato. A presto da Sfarinà!'}
              </p>
              <button type="button" onClick={onClose} data-testid="lp-lead-success-close"
                className="inline-flex items-center justify-center text-white font-['Poppins',_sans-serif] font-bold py-3 px-8 rounded-lg transition-opacity hover:opacity-90"
                style={{ background: 'var(--lp-primary)' }}>
                Chiudi
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="lp-lead-form" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-['Poppins',_sans-serif] font-semibold text-sm mb-1.5" style={{ color: 'var(--lp-fg, #1A1A1A)' }}>Nome</label>
                  <input data-testid="lp-lead-nome" type="text" value={form.nome} onChange={handleChange('nome')} required
                    className="w-full px-4 py-3 rounded-lg bg-white border outline-none transition-colors focus:ring-2"
                    style={{ borderColor: 'var(--lp-border, #E0D5C1)', '--tw-ring-color': 'var(--lp-primary)' }} />
                </div>
                <div>
                  <label className="block font-['Poppins',_sans-serif] font-semibold text-sm mb-1.5" style={{ color: 'var(--lp-fg, #1A1A1A)' }}>Cognome</label>
                  <input data-testid="lp-lead-cognome" type="text" value={form.cognome} onChange={handleChange('cognome')}
                    className="w-full px-4 py-3 rounded-lg bg-white border outline-none transition-colors focus:ring-2"
                    style={{ borderColor: 'var(--lp-border, #E0D5C1)', '--tw-ring-color': 'var(--lp-primary)' }} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-['Poppins',_sans-serif] font-semibold text-sm mb-1.5" style={{ color: 'var(--lp-fg, #1A1A1A)' }}>Email</label>
                  <input data-testid="lp-lead-email" type="email" value={form.email} onChange={handleChange('email')} required
                    className="w-full px-4 py-3 rounded-lg bg-white border outline-none transition-colors focus:ring-2"
                    style={{ borderColor: 'var(--lp-border, #E0D5C1)', '--tw-ring-color': 'var(--lp-primary)' }} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-['Poppins',_sans-serif] font-semibold text-sm mb-1.5" style={{ color: 'var(--lp-fg, #1A1A1A)' }}>Telefono (riceverai il coupon su WhatsApp)</label>
                  <input data-testid="lp-lead-telefono" type="tel" value={form.telefono} onChange={handleChange('telefono')} placeholder="+393331234567" required
                    className="w-full px-4 py-3 rounded-lg bg-white border outline-none transition-colors focus:ring-2"
                    style={{ borderColor: 'var(--lp-border, #E0D5C1)', '--tw-ring-color': 'var(--lp-primary)' }} />
                </div>
              </div>
              <label className="flex items-start gap-3 mt-5 cursor-pointer">
                <input type="checkbox" data-testid="lp-lead-privacy" checked={form.privacy} onChange={handleChange('privacy')}
                  className="mt-1 w-5 h-5 cursor-pointer" style={{ accentColor: 'var(--lp-primary)' }} />
                <span className="font-['Lora',_serif] text-xs md:text-sm leading-snug" style={{ color: 'var(--lp-muted, #5a4a42)' }}>
                  Acconsento al trattamento dei dati personali per ricevere il coupon e comunicazioni promozionali da Sfarinà.
                </span>
              </label>
              {errorMsg && (
                <div data-testid="lp-lead-error" className="mt-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-['Poppins',_sans-serif] text-sm font-semibold">
                  {errorMsg}
                </div>
              )}
              <button type="submit" disabled={submitting} data-testid="lp-lead-submit"
                className="mt-6 w-full inline-flex items-center justify-center text-white font-['Poppins',_sans-serif] font-bold py-4 px-8 rounded-lg shadow-lg transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: 'var(--lp-primary)' }}>
                {submitting ? 'Invio in corso…' : (copy?.submit || 'Sblocca la tua Bibita in Omaggio')}
              </button>
              <p className="mt-4 text-center text-xs font-['Lora',_serif]" style={{ color: 'var(--lp-muted, #5a4a42)' }}>
                I tuoi dati sono al sicuro. Puoi disiscriverti in ogni momento.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LpLeadModal;
